import type { ToastApi, ToastOptions } from '@marrafa-gioale/gymx-ui-web'

/**
 * I messaggi effimeri del pannello, sopra il `ToastProvider` del design system.
 *
 * ## Perché una funzione di modulo e non `useToast()` in ogni componente
 *
 * `useToast()` è l'uso normale del design system, e va benissimo dentro un
 * componente. Ma tre dei punti che devono mostrare un toast **non sono dentro
 * React**: l'`onError` della `QueryCache`, il listener che scopre la sessione
 * perduta e quello del cambio di organizzazione, tutti e tre costruiti in
 * `main.tsx` prima che esista un albero. Da lì un hook non è chiamabile.
 *
 * Le alternative erano due mezze soluzioni: montare quei tre punti dentro un
 * componente solo per poter usare l'hook — cioè spostare la configurazione del
 * client dentro la UI — oppure tenere due meccanismi diversi, l'hook nei
 * componenti e qualcos'altro fuori. Due meccanismi significano due formati di
 * messaggio che divergono al primo cambio.
 *
 * Quindi: il provider del design system fa il lavoro vero — la regione live,
 * l'annuncio cortese o deciso, la sospensione del timer al passaggio del
 * puntatore, il salto del focus con F8 — e questo modulo è solo l'indirizzo a
 * cui chiedere, raggiungibile da dentro e da fuori l'albero.
 */

/** Un messaggio, più la chiave che serve a non ripeterlo (vedi `key`). */
export interface Notification extends ToastOptions {
  /**
   * Sostituisce il messaggio precedente con la stessa chiave invece di
   * accodarne un secondo.
   *
   * Serve a `mfa_setup_required`: con `mfa.enforceForAdmins` attivo lo
   * restituisce **ogni** richiesta del perimetro di piattaforma, quindi al primo
   * caricamento arriverebbero sei toast identici uno sopra l'altro. Il design
   * system non deduplica da sé — un id lo genera lui — e infatti è la sola cosa
   * che questo modulo aggiunge alla sua API.
   */
  key?: string
}

let api: ToastApi | null = null

/**
 * I messaggi arrivati prima che l'albero fosse montato.
 *
 * Non è un caso ipotetico: `bootstrap()` in `main.tsx` installa i listener e fa
 * il primo `refresh` della sessione **prima** di `createRoot(...).render(...)`.
 * Un refresh che fallisce là in mezzo è esattamente il momento in cui l'utente
 * deve sapere che la sessione è finita, e senza questa coda quel messaggio
 * andrebbe perso in silenzio.
 */
const pending: Notification[] = []

/** L'id dell'ultimo messaggio mostrato per ogni chiave, per poterlo chiudere. */
const shownByKey = new Map<string, string>()

function present(notification: Notification): void {
  if (api === null) return
  const { key, ...options } = notification

  if (key === undefined) {
    api.show(options)
    return
  }

  const previous = shownByKey.get(key)
  if (previous !== undefined) api.dismiss(previous)
  shownByKey.set(key, api.show(options))
}

/** Mostra un messaggio. Chiamabile da qualunque punto, anche fuori da React. */
export function notify(notification: Notification): void {
  if (api === null) {
    pending.push(notification)
    return
  }
  present(notification)
}

/**
 * Collega il provider a `notify()`. La chiama `ToastBridge` e nessun altro.
 *
 * Sta qui e non nel file del componente perché `notify` e la coda sono stato di
 * modulo: esportarle da un file che esporta anche un componente rompe il fast
 * refresh — a ogni salvataggio il modulo verrebbe rivalutato e `api` tornerebbe
 * `null`, cioè i messaggi finirebbero nella coda invece che a schermo.
 */
export function connectToast(next: ToastApi | null): void {
  api = next
  if (next === null) {
    shownByKey.clear()
    return
  }
  while (pending.length > 0) {
    const first = pending.shift()
    if (first !== undefined) present(first)
  }
}
